import { __awaiter } from 'tslib';
import { subscribe, trigger } from '@navify-platform/event';
import { Env } from '@navify-platform/env';
import { getRequest, postRequest } from '@navify-platform/http';
import { ExtError } from '@navify-platform/error';

/**
 * Idle event type enum.
 */
var IdleEventType;
(function (IdleEventType) {
    IdleEventType["StateChange"] = "stateChange";
    IdleEventType["BeforeIdle"] = "beforeIdle";
    IdleEventType["NotBeforeIdle"] = "notBeforeIdle";
    IdleEventType["Idle"] = "idle";
    IdleEventType["NotIdle"] = "notIdle";
    IdleEventType["Active"] = "active";
    IdleEventType["NotActive"] = "notActive";
})(IdleEventType || (IdleEventType = {}));

const ɵ0 = ({ timeSinceAttempt, timeSinceCall }) => (timeSinceAttempt >= 1000 * 10 && // 10 seconds
    timeSinceCall >= 1000 * 30 // 30 seconds
), ɵ1 = ({ timeSinceAttempt, timeSinceCall, isBeforeIdle }) => (isBeforeIdle ? (timeSinceAttempt >= 1000 * 10 && // 10 seconds
    timeSinceCall >= 1000 * 10 // 10 seconds
) : (timeSinceAttempt >= 1000 * 30 && // 30 seconds
    timeSinceCall >= 1000 * 60 // 1 minutes
));
/**
 * Options default values.
 */
const IDLE_OPTIONS_DEFAULTS = {
    /** Clock sync interval default time interval. */
    syncTimeInterval: 1000 * 60 * 10,
    /** Clock tick interval default time interval. */
    tickTimeInterval: 1000 * 10,
    /** Before idle default time span. */
    beforeIdleTimeSpan: 1000 * 60,
    /** Alive call condition check function. */
    aliveCallOpt: ɵ0,
    /** Activity call condition check function. */
    lastActivityCallOpt: ɵ1,
    /** Synchronizing timeout information via API is turned on by default. */
    toggleSyncIdleTimeout: true,
    /** Synchronizing activity information via API is turned on by default. */
    toggleSyncActivity: true,
    /** DOM events tracking is turned on by default. */
    toggleTracking: true,
};
/**
 * Session timeout default value.
 */
const IDLE_TIMEOUT_DEFAULT = 1000 * 60 * 15; // 15 minutes
/**
 * Navify tenant request header name.
 */
const IDLE_NAVIFY_TENANT_HEADER_NAME = 'X-Navify-Tenant';

/**
 * @hidden
 */
function requestGetUserInfo(platformApiUrl, tenantAlias) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const url = `${platformApiUrl}/api/v1/users/whoami`;
            const headers = { [IDLE_NAVIFY_TENANT_HEADER_NAME]: tenantAlias };
            const response = yield getRequest(url, { headers });
            return response;
        }
        catch (error) {
            if (error.faults && !!error.faults.httpUnauthorized) {
                return null;
            }
            throw new ExtError({ requestGetUserInfo: true }, error);
        }
    });
}
/**
 * @hidden
 */
function getIdleTimeout(platformApiUrl, tenantAlias) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const userInfo = yield requestGetUserInfo(platformApiUrl, tenantAlias);
            const tenantsIdleTimeout = userInfo.tenants.reduce((tenantsIdleTimeoutAcc, tenant) => {
                tenantsIdleTimeoutAcc[tenant.alias] = tenant && (tenant.sessionTime * 1000 * 60) || null;
                return tenantsIdleTimeoutAcc;
            }, {});
            const idleTimeout = tenantAlias in tenantsIdleTimeout ? tenantsIdleTimeout[tenantAlias] : null;
            return { idleTimeout, tenantsIdleTimeout };
        }
        catch (error) {
            throw new ExtError({ getIdleTimeoutError: true }, error);
        }
    });
}
/**
 * @hidden
 */
function postAlive(platformApiUrl, tenantAlias, appAlias) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const url = `${platformApiUrl}/api/v1/auth/keep-alive`;
            const headers = { [IDLE_NAVIFY_TENANT_HEADER_NAME]: tenantAlias };
            const body = JSON.stringify({ appAlias });
            yield postRequest(url, { headers, body });
        }
        catch (error) {
            throw new ExtError({ postAliveError: true }, error);
        }
    });
}
;
/**
 * @hidden
 */
function getLastActivity(platformApiUrl, tenantAlias) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const url = `${platformApiUrl}/api/v1/auth/last-seen`;
            const headers = { [IDLE_NAVIFY_TENANT_HEADER_NAME]: tenantAlias };
            const response = yield getRequest(url, { headers });
            const activityTimestamp = response.lastSeen * 1000;
            return activityTimestamp;
        }
        catch (error) {
            throw new ExtError({ getLastActivityError: true }, error);
        }
    });
}
;

/**
 * Idle class.
 */
class Idle {
    constructor(idleOptions) {
        this.idleOptions = idleOptions;
        this.syncIntervalId = null;
        this.tickIntervalId = null;
        this.mousemoveHandler = null;
        this.clickHandler = null;
        this.keypressHandler = null;
        this.idleTimeout = null;
        this.nowTimestamp = null;
        this.lastActivityTimestamp = null;
        this.lastOwnActivityTimestamp = null;
        this.idleTimeValue = null;
        this.isBeforeIdleValue = null;
        this.isIdleValue = null;
        this.isActiveValue = null;
        this.lastActivityAttemptTimestamp = null;
        this.lastActivityCallTimestamp = null;
        this.aliveAttemptTimestamp = null;
        this.aliveCallTimestamp = null;
        this.eventHandlers = [];
        this.isSyncIdleTimeoutValue = null;
        this.isSyncActivityValue = null;
        this.isTrackingValue = null;
    }
    static init(idleOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const idle = new Idle(idleOptions);
            yield idle.init();
            return idle;
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.initOptions();
            yield this.initEnv();
            yield this.haveIdleTimeout();
            window.addEventListener('mousemove', this.mousemoveHandler = () => this.handleMousemove());
            window.addEventListener('click', this.clickHandler = () => this.handleClick());
            window.addEventListener('keypress', this.keypressHandler = () => this.handleKeypress());
            this.syncIntervalId = window.setInterval(() => this.syncTime(), this.idleOptions.syncTimeInterval);
            yield this.syncTime();
            this.tickIntervalId = window.setInterval(() => this.tickTime(), this.idleOptions.tickTimeInterval);
            yield this.tickTime();
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            window.clearInterval(this.syncIntervalId);
            window.clearInterval(this.tickIntervalId);
            window.removeEventListener('mousemove', this.mousemoveHandler);
            window.removeEventListener('click', this.clickHandler);
            window.removeEventListener('keypress', this.keypressHandler);
        });
    }
    initOptions() {
        return __awaiter(this, void 0, void 0, function* () {
            this.idleOptions = Object.assign(Object.assign({}, IDLE_OPTIONS_DEFAULTS), (this.idleOptions || {}));
            this.isSyncIdleTimeoutValue = this.idleOptions.toggleSyncIdleTimeout;
            this.isSyncActivityValue = this.idleOptions.toggleSyncActivity;
            this.isTrackingValue = this.idleOptions.toggleTracking;
        });
    }
    initEnv() {
        return __awaiter(this, void 0, void 0, function* () {
            this.env = this.idleOptions.env || new Env();
        });
    }
    haveIdleTimeout() {
        return __awaiter(this, void 0, void 0, function* () {
            const idleTimeoutOption = this.idleOptions.idleTimeout;
            if (idleTimeoutOption !== undefined) {
                this.idleTimeout = idleTimeoutOption;
                return;
            }
            if (!this.isSyncIdleTimeoutValue) {
                this.idleTimeout = this.idleTimeout || IDLE_TIMEOUT_DEFAULT;
                return;
            }
            try {
                const platformApiUrl = yield this.env.getPlatformApiUrl();
                const tenantAlias = yield this.env.getTenantAlias();
                const { idleTimeout, tenantsIdleTimeout } = yield getIdleTimeout(platformApiUrl, tenantAlias);
                if (idleTimeout) {
                    this.idleTimeout = idleTimeout;
                }
                else {
                    const idleTimeoutList = Object.keys(tenantsIdleTimeout).map((tenant) => tenantsIdleTimeout[tenant]);
                    const idleTimeoutMin = idleTimeoutList.reduce((timeoutAcc, timeoutAny) => {
                        if (timeoutAcc && timeoutAny) {
                            return Math.min(timeoutAcc, timeoutAny);
                        }
                        return timeoutAcc || timeoutAny || null;
                    }, null);
                    this.idleTimeout = idleTimeoutMin || IDLE_TIMEOUT_DEFAULT;
                }
            }
            catch (error) {
                this.idleTimeout = IDLE_TIMEOUT_DEFAULT;
            }
        });
    }
    handleMousemove() {
        if (this.isTrackingValue) {
            this.markActivity();
        }
    }
    handleClick() {
        if (this.isTrackingValue) {
            this.markActivity();
        }
    }
    handleKeypress() {
        if (this.isTrackingValue) {
            this.markActivity();
        }
    }
    syncTime() {
        this.nowTimestamp = Date.now();
    }
    tickTime() {
        return __awaiter(this, void 0, void 0, function* () {
            this.nowTimestamp += this.idleOptions.tickTimeInterval;
            this.evalIdleTime();
            yield this.checkActivity();
            yield this.checkCallAlive();
        });
    }
    evalIdleTime() {
        if (this.lastActivityTimestamp === null) {
            return null;
        }
        this.idleTimeValue = this.nowTimestamp - this.lastActivityTimestamp;
    }
    checkActivity() {
        return __awaiter(this, void 0, void 0, function* () {
            const { isChanged } = this.evalActivity();
            const { lastActivityCallOpt } = this.idleOptions;
            const activityCallOption = yield lastActivityCallOpt({
                timeSinceAttempt: this.nowTimestamp - (this.lastActivityAttemptTimestamp || 0),
                timeSinceCall: this.nowTimestamp - (this.lastActivityCallTimestamp || 0),
                isActive: this.isActiveValue,
                isBeforeIdle: this.isBeforeIdleValue,
                isIdle: this.isIdleValue,
                timeSinceActivity: this.nowTimestamp - (this.lastActivityTimestamp || 0),
                timeToIdle: this.idleTimeout - (this.idleTimeValue || 0),
            });
            if (isChanged || activityCallOption) {
                yield this.haveLastActivity();
                this.evalIdleTime();
                yield this.applyActivity();
            }
        });
    }
    evalActivity() {
        let isActive = null;
        let isBeforeIdle = null;
        let isIdle = null;
        if (this.idleTimeout !== null && this.idleTimeValue !== null) {
            const timeToIdle = (this.idleTimeout - this.idleTimeValue);
            isActive = (timeToIdle >= 0);
            isBeforeIdle = (timeToIdle >= 0) && (timeToIdle < this.idleOptions.beforeIdleTimeSpan);
            isIdle = (timeToIdle < 0);
        }
        const isActiveChanged = (isActive !== this.isActiveValue);
        const isBeforeIdleChanged = (isBeforeIdle !== this.isBeforeIdleValue);
        const isIdleChanged = (isIdle !== this.isIdleValue);
        const isChanged = (isActiveChanged || isBeforeIdleChanged || isIdleChanged);
        return {
            isActive,
            isActiveChanged,
            isBeforeIdle,
            isBeforeIdleChanged,
            isIdle,
            isIdleChanged,
            isChanged,
        };
    }
    applyActivity() {
        return __awaiter(this, void 0, void 0, function* () {
            const { isActive, isActiveChanged, isBeforeIdle, isBeforeIdleChanged, isIdle, isIdleChanged, isChanged, } = this.evalActivity();
            const eventData = {
                isActive,
                isActiveChanged,
                isBeforeIdle,
                isBeforeIdleChanged,
                isIdle,
                isIdleChanged,
            };
            if (isChanged) {
                yield this.trigger(IdleEventType.StateChange, eventData);
            }
            if (isActiveChanged) {
                this.isActiveValue = isActive;
                yield this.trigger(isActive ? IdleEventType.Active : IdleEventType.NotActive, eventData);
            }
            if (isBeforeIdleChanged) {
                this.isBeforeIdleValue = isBeforeIdle;
                yield this.trigger(isBeforeIdle ? IdleEventType.BeforeIdle : IdleEventType.NotBeforeIdle, eventData);
            }
            if (isIdleChanged) {
                this.isIdleValue = isIdle;
                yield this.trigger(isIdle ? IdleEventType.Idle : IdleEventType.NotIdle, eventData);
            }
        });
    }
    haveLastActivity() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isSyncActivityValue) {
                return;
            }
            try {
                const platformApiUrl = yield this.env.getPlatformApiUrl();
                const tenantAlias = yield this.env.getTenantAlias();
                this.lastActivityAttemptTimestamp = this.nowTimestamp;
                const lastActivityTimestamp = yield getLastActivity(platformApiUrl, tenantAlias);
                this.lastActivityCallTimestamp = this.nowTimestamp;
                if (lastActivityTimestamp && lastActivityTimestamp > (this.lastActivityTimestamp || 0)) {
                    this.lastActivityTimestamp = lastActivityTimestamp;
                }
            }
            catch (error) {
                // noop
            }
        });
    }
    checkCallAlive() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isSyncActivityValue) {
                return;
            }
            if (!this.lastOwnActivityTimestamp ||
                !this.isActiveValue ||
                this.lastOwnActivityTimestamp <= (this.aliveCallTimestamp || 0)) {
                return;
            }
            const { aliveCallOpt } = this.idleOptions;
            const aliveCallOption = yield aliveCallOpt({
                timeSinceAttempt: this.nowTimestamp - (this.aliveAttemptTimestamp || 0),
                timeSinceCall: this.nowTimestamp - (this.aliveCallTimestamp || 0),
            });
            if (aliveCallOption) {
                try {
                    const platformApiUrl = yield this.env.getPlatformApiUrl();
                    const tenantAlias = yield this.env.getTenantAlias();
                    const appAlias = yield this.env.getAppAlias();
                    this.aliveAttemptTimestamp = this.nowTimestamp;
                    yield postAlive(platformApiUrl, tenantAlias, appAlias);
                    this.aliveCallTimestamp = this.nowTimestamp;
                }
                catch (error) {
                    // noop
                }
            }
        });
    }
    markActivity() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.lastActivityTimestamp !== this.nowTimestamp) {
                this.lastActivityTimestamp = this.nowTimestamp;
                this.lastOwnActivityTimestamp = this.nowTimestamp;
                this.evalIdleTime();
                yield this.checkActivity();
                yield this.checkCallAlive();
            }
        });
    }
    get isIdle() {
        return this.isIdleValue;
    }
    get isActive() {
        return this.isActiveValue;
    }
    subscribe(eventHandler) {
        const { eventHandlers, unsubscribe } = subscribe(this.eventHandlers, eventHandler);
        this.eventHandlers = eventHandlers;
        return unsubscribe;
    }
    trigger(type, data = null, result = null) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield trigger(this.eventHandlers, type, data, result);
        });
    }
    toggleSyncIdleTimeout(isSyncIdleTimeout) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isSyncIdleTimeoutValue = isSyncIdleTimeout;
            if (isSyncIdleTimeout) {
                yield this.haveIdleTimeout();
            }
        });
    }
    get isSyncIdleTimeout() {
        return this.isSyncIdleTimeoutValue;
    }
    toggleSyncActivity(isSyncActivity) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isSyncActivityValue = isSyncActivity;
            if (isSyncActivity) {
                yield this.checkActivity();
                yield this.checkCallAlive();
            }
        });
    }
    get isSyncActivity() {
        return this.isSyncActivityValue;
    }
    toggleTracking(isTracking) {
        this.isTrackingValue = isTracking;
    }
    get isTracking() {
        return this.isTrackingValue;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { IDLE_NAVIFY_TENANT_HEADER_NAME, IDLE_OPTIONS_DEFAULTS, IDLE_TIMEOUT_DEFAULT, Idle, IdleEventType, getIdleTimeout, getLastActivity, postAlive, requestGetUserInfo, ɵ0, ɵ1 };

